import { DashboardScatterPlotChart } from "@features/results/Result/Dashboard/DashboardScatterPlotChart"
import { DashboardProbabilityDensityChart } from "@features/results/Result/Dashboard/DashboardProbabilityDensityChart"
import { DashboardTimeSeriesChart } from "@features/results/Result/Dashboard/DashboardTimeSeriesChart"
import { DashboardAggregateChart } from "@features/results/Result/Dashboard/DashboardAggregateChart"
import { FunctionComponent } from "react"
import { useGetRunsByKeysQuery } from "@store/services/runService"
import { Box, CircularProgress, SxProps } from "@mui/material"
import { useGetGraphDataQuery, useGetUserGraphDataQuery } from "@store/services/graphsService"
import { DashboardBarChart } from "@features/results/Result/Dashboard/DashboardBarChart"
import { DashboardBarCategoricalChart } from "@features/results/Result/Dashboard/DashboardBarCategoricalChart"
import {
  ChartGroupLayout,
  DashboardChartDto,
  DashboardChartTypeDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import DashboardThresholdChart from "@features/results/Result/Dashboard/DashboardThresholdChart"

import { DashboardTableChart } from "@features/results/Result/Dashboard/DashboardTableChart"
import { Provider } from "jotai"
import { DashboardFullScreenDialog } from "@features/results/Result/Dashboard/DashboardFullScreenDialog"

interface Props {
  charts: DashboardChartDto[]
  layout?: ChartGroupLayout
  runKeys: string[]
  selectedRunKey: string
}

export const DrawerChartList: FunctionComponent<Props> = ({
  charts,
  runKeys,
  selectedRunKey,
  layout,
}) => {
  const { data: runsData, isFetching: isFetchingRunData } = useGetRunsByKeysQuery(runKeys)
  const selectedRunData = runsData?.[selectedRunKey]
  const runs = Object.values(runsData ?? {})

  const baseGraphDataQuery = useGetGraphDataQuery(selectedRunData?.base_graph_key ?? "", {
    skip: !selectedRunData?.base_graph_key,
  })

  const userGraphDataQuery = useGetUserGraphDataQuery(selectedRunData?.user_graph_key ?? "", {
    skip: !selectedRunData?.user_graph_key,
  })

  if (!selectedRunData || isFetchingRunData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  const graphData = selectedRunData?.user_graph_key ? userGraphDataQuery : baseGraphDataQuery

  if (!graphData?.data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  const containerSx: SxProps = layout
    ? {
        display: "grid",
        width: "100%",
        height: `${layout.Height ?? 100}vh`,
        gridTemplateColumns: `repeat(${layout.NumberOfColumns ?? 100}, 1fr)`,
        gridTemplateRows: `repeat(${layout.NumberOfRows ?? 100}, 1fr)`,
      }
    : {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        flexWrap: "wrap",
      }

  return (
    <Box sx={containerSx}>
      {charts.map((chart) => (
        <Provider key={chart.uid ?? ""}>
          {chart.Type === DashboardChartTypeDto.ScatterPlot && (
            <>
              <DashboardScatterPlotChart chart={chart} runs={runs} layout={layout} />
              <DashboardFullScreenDialog>
                <DashboardScatterPlotChart chart={chart} runs={runs} />
              </DashboardFullScreenDialog>
            </>
          )}
          {chart.Type === DashboardChartTypeDto.ProbabilityDensity && (
            <>
              <DashboardProbabilityDensityChart chart={chart} runs={runs} layout={layout} />
              <DashboardFullScreenDialog>
                <DashboardProbabilityDensityChart chart={chart} runs={runs} />
              </DashboardFullScreenDialog>
            </>
          )}
          {chart.Type === DashboardChartTypeDto.TimeSeries && (
            <>
              <DashboardTimeSeriesChart chart={chart} runs={runs} layout={layout} />
              <DashboardFullScreenDialog>
                <DashboardTimeSeriesChart chart={chart} runs={runs} />
              </DashboardFullScreenDialog>
            </>
          )}
          {chart.Type === DashboardChartTypeDto.BarChart && (
            <>
              <DashboardBarChart
                chart={chart}
                runs={runs}
                graphData={graphData.data!}
                layout={layout}
              />
              <DashboardFullScreenDialog>
                <DashboardBarChart chart={chart} runs={runs} graphData={graphData.data!} />
              </DashboardFullScreenDialog>
            </>
          )}
          {chart.Type === DashboardChartTypeDto.BarCategoricalChart && (
            <>
              <DashboardBarCategoricalChart chart={chart} runs={runs} layout={layout} />
              <DashboardFullScreenDialog>
                <DashboardBarCategoricalChart chart={chart} runs={runs} />
              </DashboardFullScreenDialog>
            </>
          )}
          {chart.Type === DashboardChartTypeDto.Threshold && (
            <>
              <DashboardThresholdChart
                chart={chart}
                run={selectedRunData}
                graphData={graphData.data!}
                layout={layout}
              />
              <DashboardFullScreenDialog>
                <DashboardThresholdChart
                  chart={chart}
                  run={selectedRunData}
                  graphData={graphData.data!}
                />
              </DashboardFullScreenDialog>
            </>
          )}
          {chart.Type === DashboardChartTypeDto.Aggregate && (
            <>
              <DashboardAggregateChart chart={chart} runs={runs} layout={layout} />
              <DashboardFullScreenDialog>
                <DashboardAggregateChart chart={chart} runs={runs} />
              </DashboardFullScreenDialog>
            </>
          )}
          {chart.Type === DashboardChartTypeDto.Table && (
            <>
              <DashboardTableChart
                chart={chart}
                layout={layout}
                runs={runs}
                selectedRunKey={selectedRunKey}
              />
              <DashboardFullScreenDialog>
                <DashboardTableChart chart={chart} runs={runs} selectedRunKey={selectedRunKey} />
              </DashboardFullScreenDialog>
            </>
          )}
        </Provider>
      ))}
    </Box>
  )
}
